import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import type {
  ExchangeRate,
  ExchangeRateCurrency,
  ExchangeRateHistoryItem,
  ExchangeRateProviderConfig,
  ExchangeRateProviderConfigPayload,
  ExchangeRateProviderTestResult,
  ExchangeRateRefreshResult,
  ExchangeRateUpdatePayload,
} from '../types';

/**
 * 환율 설정 API. path/shape 는 be-catalog(ID19) 계약과 정합한다.
 * 다른 admin 도메인과 동일하게 envelope { success, data } 를 adminFetch 로 호출한다.
 */
const BASE = '/admin/v1/exchange-rates';

interface ListEnvelope {
  success: boolean;
  data?: ExchangeRate[] | null;
}

interface SingleEnvelope {
  success: boolean;
  data?: ExchangeRate | null;
}

interface ProviderEnvelope {
  success: boolean;
  data?: ExchangeRateProviderConfig | null;
}

interface ProviderTestEnvelope {
  success: boolean;
  data?: ExchangeRateProviderTestResult | null;
}

interface HistoryEnvelope {
  success: boolean;
  data?: ExchangeRateHistoryItem[] | null;
}

interface RefreshEnvelope {
  success: boolean;
  data?: ExchangeRateRefreshResult | null;
}

export const exchangeRatesQueryKey = () => ['admin', 'exchange-rates'] as const;
export const exchangeRateProviderQueryKey = () => ['admin', 'exchange-rate-provider'] as const;
export const exchangeRateHistoryQueryKey = () => ['admin', 'exchange-rate-history'] as const;

const fetchList = async (): Promise<ExchangeRate[]> => {
  const res = await adminFetch(BASE);
  if (!res.ok) throw new Error(`exchange rates list failed: ${res.status}`);
  const env = (await res.json()) as ListEnvelope;
  return env.data ?? [];
};

const fetchProvider = async (): Promise<ExchangeRateProviderConfig> => {
  const res = await adminFetch(`${BASE}/provider`);
  if (!res.ok) throw new Error(`exchange rate provider fetch failed: ${res.status}`);
  const env = (await res.json()) as ProviderEnvelope;
  if (!env.data) throw new Error('no data');
  return env.data;
};

const fetchHistory = async (): Promise<ExchangeRateHistoryItem[]> => {
  const res = await adminFetch(`${BASE}/history`);
  if (!res.ok) throw new Error(`exchange rate history fetch failed: ${res.status}`);
  const env = (await res.json()) as HistoryEnvelope;
  return env.data ?? [];
};

export const useExchangeRatesQuery = () =>
  useQuery<ExchangeRate[]>({
    queryKey: exchangeRatesQueryKey(),
    queryFn: fetchList,
  });

export const useExchangeRateProviderQuery = () =>
  useQuery<ExchangeRateProviderConfig>({
    queryKey: exchangeRateProviderQueryKey(),
    queryFn: fetchProvider,
  });

export const useExchangeRateHistoryQuery = () =>
  useQuery<ExchangeRateHistoryItem[]>({
    queryKey: exchangeRateHistoryQueryKey(),
    queryFn: fetchHistory,
  });

export const useExchangeRateMutation = () => {
  const qc = useQueryClient();

  const invalidateAll = () => {
    void qc.invalidateQueries({ queryKey: exchangeRatesQueryKey() });
    void qc.invalidateQueries({ queryKey: exchangeRateHistoryQueryKey() });
  };

  const update = useMutation({
    mutationFn: async ({
      currencyCode,
      payload,
    }: {
      currencyCode: ExchangeRateCurrency;
      payload: ExchangeRateUpdatePayload;
    }): Promise<ExchangeRate> => {
      const res = await adminFetch(`${BASE}/${currencyCode}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`exchange rate update failed: ${res.status}`);
      const env = (await res.json()) as SingleEnvelope;
      if (!env.data) throw new Error('no data');
      return env.data;
    },
    onSuccess: invalidateAll,
  });

  return { update };
};

export const useExchangeRateProviderMutation = () => {
  const qc = useQueryClient();

  const save = useMutation({
    mutationFn: async (
      payload: ExchangeRateProviderConfigPayload,
    ): Promise<ExchangeRateProviderConfig> => {
      const res = await adminFetch(`${BASE}/provider`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`exchange rate provider save failed: ${res.status}`);
      const env = (await res.json()) as ProviderEnvelope;
      if (!env.data) throw new Error('no data');
      return env.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: exchangeRateProviderQueryKey() }),
  });

  /**
   * 연동 테스트 — 관세청 라이브 호출 후 샘플 환율 반환. persist 하지 않는다.
   * BE 가 실패를 4xx 로 내릴 수도, 200 + {success:false} 로 내릴 수도 있어 둘 다 흡수한다.
   */
  const test = useMutation({
    mutationFn: async (): Promise<ExchangeRateProviderTestResult> => {
      const res = await adminFetch(`${BASE}/provider/test`, {
        method: 'POST',
      });
      const env = (await res.json().catch(() => null)) as ProviderTestEnvelope | null;
      if (!res.ok) {
        return {
          success: false,
          message: env?.data?.message ?? `연동 테스트 실패 (${res.status})`,
          sampleRates: null,
        };
      }
      if (!env?.data) {
        return { success: false, message: '응답이 비어 있습니다.', sampleRates: null };
      }
      return env.data;
    },
  });

  return { save, test };
};

export const useExchangeRateRefreshMutation = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<ExchangeRateRefreshResult> => {
      const res = await adminFetch(`${BASE}/refresh`, {
        method: 'POST',
      });
      if (!res.ok) throw new Error(`exchange rate refresh failed: ${res.status}`);
      const env = (await res.json().catch(() => null)) as RefreshEnvelope | null;
      return (
        env?.data ?? { success: true, updatedCount: 0, message: null, rates: [] }
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: exchangeRatesQueryKey() });
      void qc.invalidateQueries({ queryKey: exchangeRateHistoryQueryKey() });
      void qc.invalidateQueries({ queryKey: exchangeRateProviderQueryKey() });
    },
  });
};
